import React, { Fragment, useMemo, useRef } from "react";
import { Link } from "react-router";
import chevronLeft from "@assets/icon-chevron-left-white.svg";
import chevronRight from "@assets/icon-chevron-right-white.svg";
import { REMOVE_FOCUS_CSS } from "@components/Button/utils/constants";
import useSendPulseGaData from "@features/pulse/hooks/useSendPulseGaData";
import type { PulsePageControlContentProps } from "@features/pulse/utils/types";
import PulseEndingScreen from "@features/pulse/variant/control/components/PulseEndingScreen";
import {
  Options,
  Splide,
  SplideSlide,
  SplideTrack,
} from "@greglaisph/react-splide";
import { cn } from "@util/helpers";

import PulseIndividualMobileArticle from "./PulseIndividualMobileArticle";

type PulseMobileCarouselProps = Omit<
  PulsePageControlContentProps,
  "handleNavigateLeftCard" | "handleNavigateRightCard"
>;

export default function PulseMobileCarousel({
  sortedPulseArticles,
  activeArticle,
  handleSetActiveArticle,
  backToBt = "/",
}: PulseMobileCarouselProps): React.ReactElement {
  const { sendCustomEvent } = useSendPulseGaData();

  const memorisedArticles = useMemo(
    () => sortedPulseArticles,
    [sortedPulseArticles]
  );

  const totalLength = memorisedArticles.length + 1;

  const options: Options = {
    perMove: 1,
    perPage: 1,
    arrows: false,
    pagination: false,
    speed: 700,
  };

  const cardsRef = useRef<Splide>(null);

  const handleOnClickGaTracking = (eventAction: string, eventLabel = "") => {
    sendCustomEvent(eventAction, eventLabel);
  };

  const handleOnMoved = (index: number, prev: number) => {
    const layer = window.dataLayer as { eventAction: string }[];
    const layerFind = layer.slice(-1);

    if (!layerFind?.[0]?.eventAction) {
      const displayTitle = sortedPulseArticles[prev].title;
      const text = index > prev ? "right" : "left";
      handleOnClickGaTracking(
        `mobile::swipe ${text}::${prev + 1}`,
        displayTitle
      );
    }

    handleSetActiveArticle(index);
  };

  if (sortedPulseArticles.length === 0) return <> </>;

  const handleNextSlide = () => {
    const currentId = activeArticle; // add 1 to activeArticle as GA index starts from 1

    const displayTitle = sortedPulseArticles[activeArticle - 1].title;
    handleOnClickGaTracking(`mobile::click next::${currentId}`, displayTitle);

    cardsRef.current?.go(activeArticle);
  };

  const handlePrevSlides = () => {
    cardsRef.current?.go(activeArticle - 2);

    const currentId = activeArticle; // add 1 to activeArticle as GA index starts from 1
    const displayTitle = sortedPulseArticles[activeArticle - 1].title;
    handleOnClickGaTracking(
      `mobile::click previous::${currentId}`,
      displayTitle
    );
  };

  return (
    <div
      className="relative flex h-svh w-screen flex-col"
      data-testid="pulse-mobile-carousel-component"
    >
      <div className="relative z-10 mt-2 flex h-[2px] w-full gap-1 px-2">
        {Array.from({ length: totalLength }).map((_, index) => {
          return (
            <div
              key={`pulse-mobile-carousel-pagination-${index}`}
              className={cn("h-[2px] bg-gray-650")}
              style={{
                width: `calc(100%/${memorisedArticles.length})`,
              }}
            >
              <div
                className={cn(
                  "h-[2px] w-0 bg-white transition-all duration-150",
                  {
                    "w-full": index <= activeArticle - 1,
                  }
                )}
              />
            </div>
          );
        })}
      </div>

      <div className="absolute top-0 h-svh">
        {memorisedArticles.map((article, index) => {
          return (
            <div
              key={`pulse-mobile-carousel-bg-${article.id}`}
              className={cn(
                "absolute top-0 h-svh w-screen bg-[#212121] opacity-0 transition-all duration-700",
                {
                  "opacity-100": activeArticle - 1 === index,
                }
              )}
              style={{
                background: article.media
                  ? `center / cover no-repeat url(${article.media}?w=800&dpr=1&f=webp)`
                  : "#212121",
              }}
            >
              <div className="absolute top-0 h-full w-full bg-gradient-to-b from-black to-80% opacity-70" />
            </div>
          );
        })}
      </div>

      <div className="relative top-0 flex-1 flex-grow items-center justify-center">
        <Splide
          className="h-full"
          options={options}
          hasTrack={false}
          onMoved={(_, index, prev) => {
            handleOnMoved(index, prev);
          }}
          ref={cardsRef}
        >
          <SplideTrack className="h-full">
            {memorisedArticles.map((article, index) => {
              return (
                <Fragment key={article.id}>
                  <SplideSlide
                    className={cn(
                      REMOVE_FOCUS_CSS,
                      "group h-full !transition-all duration-700"
                    )}
                    data-article-id={article.id}
                    data-slide-index={index}
                  >
                    <PulseIndividualMobileArticle article={article} />
                  </SplideSlide>
                </Fragment>
              );
            })}

            <SplideSlide className={cn(REMOVE_FOCUS_CSS, "group")}>
              <div className="flex h-full flex-col items-center justify-center p-4">
                <PulseEndingScreen
                  activeArticle={activeArticle}
                  backToBt={backToBt}
                />
              </div>
            </SplideSlide>
          </SplideTrack>
        </Splide>
      </div>

      <div className="relative flex  w-full items-center justify-between bg-black px-3 py-4">
        <Link
          reloadDocument
          to={`${backToBt}?ref=pulse`}
          className="inline-flex w-full items-center font-poppins text-sm font-semibold tracking-2% text-white"
          onClick={() => {
            handleOnClickGaTracking("back to bt");
          }}
        >
          <img
            src={chevronLeft}
            className="h-6 w-6"
            alt="left arrow"
            width={24}
            height={24}
          />
          Back to BT
        </Link>

        <div className="flex w-full justify-end">
          <button
            className={cn(
              REMOVE_FOCUS_CSS,
              "flex flex-none items-center gap-2 font-poppins text-sm font-semibold tracking-2% text-white transition-all duration-300",
              { "pointer-events-none opacity-50": activeArticle === 1 }
            )}
            onClick={handlePrevSlides}
          >
            <img
              src={chevronLeft}
              className="h-6 w-6"
              alt="right arrow"
              width={24}
              height={24}
            />
            Previous
          </button>

          <div className="mx-3 h-auto w-[1px] flex-none bg-gray-750"></div>
          <button
            onClick={handleNextSlide}
            className={cn(
              REMOVE_FOCUS_CSS,
              "flex flex-none items-center gap-2 font-poppins text-sm font-semibold tracking-2% text-white transition-all duration-300",
              {
                "pointer-events-none opacity-50": activeArticle === totalLength,
              }
            )}
          >
            Next
            <img
              src={chevronRight}
              className="h-6 w-6"
              alt="left arrow"
              width={24}
              height={24}
            />
          </button>
        </div>
      </div>
    </div>
  );
}
